import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { VncModalSelectors } from '../../state/vncModalSelectors';
import { VncModalActions } from '../../state/vncModalSlice';
import { StyledVncWarningOneIsOpenAlreadyModal } from './VncWarningOneIsOpenAlreadyModal.styles';
import { PrimaryButton } from 'lib/components/Button/PrimaryButton/PrimaryButton';
import { SecondaryButton } from 'lib/components/Button/SecondaryButton/SecondaryButton';
import { VncActions } from 'app/components/Vnc/state/vncSlice';
import {
  LOCAL_STORAGE_VNC_KEY,
  LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY,
  LOCAL_STORAGE_VNC_SERIAL_NUMBER,
} from 'config/constants';

export const VncWarningOneIsOpenAlreadyModal = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isModalVisible = !!useSelector(VncModalSelectors.selectVncModalWarningOneIsOpenAlreadyIsVisible);
  const machineId = useSelector(VncModalSelectors.selectVncModalWarningOneIsOpenAlreadyMachineId) || '';
  const softwareVersion = useSelector(VncModalSelectors.selectVncModalWarningOneIsOpenAlreadySoftwareVersion);
  const serialNumber = useSelector(VncModalSelectors.selectVncModalWarningOneIsOpenAlreadySerialNumber) || '';

  const handleVncWindowReset = useCallback(() => {
    dispatch(VncActions.vncResetWindow());
    dispatch(VncActions.vncOpenWindow());
  }, [dispatch]);

  const handleVncCancelUrlAndStatus = useCallback(() => {
    dispatch(VncActions.vncRequestUrlV2Cancel());
    dispatch(VncActions.vncGetUrlStatusV2Cancel());
  }, [dispatch]);

  const setVncInformationLocalStorage = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_VNC_KEY, machineId);
    localStorage.setItem(LOCAL_STORAGE_VNC_SERIAL_NUMBER, serialNumber);
    localStorage.setItem(LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY, softwareVersion || '');
  }, [machineId, serialNumber, softwareVersion]);

  const handleEffectiveRequestVncSession = useCallback(
    () =>
      dispatch(
        VncActions.vncRequestUrlV2Request({
          machineId,
          softwareVersion,
        })
      ),
    [dispatch, machineId, softwareVersion]
  );

  /**
   * @context pop up message to let user know that there is a VNC session running
   * @description Handle action to close warning message about already one vnc is running
   */
  const handleHideModal = useCallback(() => {
    dispatch(VncModalActions.vncModalWarningOneIsOpenAlreadyHide());
  }, [dispatch]);

  /**
   * @context pop up message to let user know that there is a VNC session running
   * @description Handle action to close current vnc session and open new vnc session with new machine
   */
  const handleRequestVncUrl = useCallback(() => {
    if (!machineId) {
      return;
    }

    dispatch(VncModalActions.vncModalWarningOneIsOpenAlreadyHide());
    handleVncCancelUrlAndStatus();
    handleVncWindowReset();
    setVncInformationLocalStorage();
    handleEffectiveRequestVncSession();
  }, [
    dispatch,
    machineId,
    handleEffectiveRequestVncSession,
    setVncInformationLocalStorage,
    handleVncCancelUrlAndStatus,
    handleVncWindowReset,
  ]);

  return (
    <StyledVncWarningOneIsOpenAlreadyModal
      title={t('vnc.modals.oneIsOpenAlready.title')}
      open={isModalVisible}
      className="vnc-warning-one-is-open-already-modal"
      closable={false}
      width={550}
      destroyOnClose
      footer={[
        <SecondaryButton size="m" key="cancel-button" className="cancel-button" onClick={handleHideModal}>
          {t('common.cancel')}
        </SecondaryButton>,
        <PrimaryButton size="m" key="submit-button" className="submit-button" onClick={handleRequestVncUrl}>
          {t('vnc.request')}
        </PrimaryButton>,
      ]}
    >
      <p className="vnc-warning-one-is-open-already-modal__description">
        {t('vnc.modals.oneIsOpenAlready.description')}
      </p>
    </StyledVncWarningOneIsOpenAlreadyModal>
  );
};
